import React, { useEffect, useState } from "react";
import { useAllUsers } from "@/hooks/queries/useUser";
import {
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
} from "@/hooks/mutations/users";
import { useViewUser } from "@/hooks/queries/useUser";
import { Building, Edit, SearchIcon, Trash2 } from "lucide-react";
import Loader from "@/components/Loader";
import { Search } from "lucide-react";
import ApplicationFilters from "@/components/Companies/ApplicationFilters";
import Pagination from "@/components/Common/Pagination";
import roleService from "@/services/roleService";
import Dropdown from "@/components/ui/Dropdown";
import UserForm from "@/pages/protected/Users/Form";
import { useDebounce } from "@/utils/helpers";
import useToastStore from "@/components/Common/Toast/toastStore";
import { PER_PAGE } from "@/config";
import DialogBox from "@/components/Common/DialogBox";

const Users = () => {
  // Initialize serverPagination first
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: PER_PAGE,
    total: 0,
    last_page: 1,
  });

  const [searchQuery, setSearchQuery] = useState("");

  // Now we can use serverPagination since it's initialized
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useAllUsers(
    {
      page: serverPagination.current_page,
      per_page: serverPagination.per_page || PER_PAGE,
      search: searchQuery,
    },
    {
      enabled: true,
      keepPreviousData: true,
    },
  );
  const [filters, setFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const { mutate: createUser, isPending: isCreating } = useCreateUser();
  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser();
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();
  // const { data: user, isLoading: isViewing } = useViewUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PER_PAGE;
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = useState(false);

  const defaultUser = {
    name: "",
    email: "",
    roles: "",
    password: "",
    color: "",
    city: "",
    country: "",
  };
  const [thisUser, setThisUser] = useState({ ...defaultUser });

  const { showToast } = useToastStore;

  const handleSubmitUser = () => {
    if (thisUser?.id) handleUpdateUser();
    else handleCreateUser();
  };

  const handleCreateUser = () => {
    createUser(thisUser, {
      onSuccess: () => {
        setIsModalOpen(false);
        setThisUser({ ...defaultUser });
        refetch();
        showToast({
          message: "User created successfully!",
          type: "success",
        });
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred";
        showToast({
          message: errorMessage,
          type: "error",
        });
      },
    });
  };

  const handleDeleteUser = (userId) => {
    setIsDeleteButtonDisabled(true);
    deleteUser(userId, {
      onSuccess: () => {
        refetch();
        setUserToDelete(null);
        showToast({
          message: "User deleted successfully!",
          type: "success",
        });
      },
      onError: (error) => {
        showToast({
          message: error.response?.data?.message || "Failed to delete user.",
          type: "error",
        });
      },
      onSettled: () => {
        setIsDeleteButtonDisabled(false);
      },
    });
  };

  // function to handle search term input
  const updateSearchTerm = (term) => {
    setSearchQuery(term);
    setServerPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // defined useDebounce for search term input
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  // Ensure users is an array
  const users = Array.isArray(usersData?.data) ? usersData.data : [];
  const columns = [
    { key: "name", label: "Name", filterable: true },
    { key: "email", label: "Email", filterable: true },
    { key: "role", label: "Role", filterable: true },
    { key: "city", label: "City", filterable: true },
    { key: "country", label: "Country", filterable: true },
  ];

  // Remove a filter
  const removeFilter = (filterKey) => {
    const newActiveFilters = activeFilters.filter((key) => key !== filterKey);
    setActiveFilters(newActiveFilters);

    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  // Filter and sort users
  useEffect(() => {
    if (users.length > 0) {
      console.log("Processing users:", users);
      console.log("Sample user object:", users[0]);
      let filtered = [...users];
      if (searchQuery) {
        filtered = filtered.filter(
          (user) =>
            user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.roles?.[0]?.title
              ?.toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            user.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.country?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }

      // Sorting logic
      if (sortConfig.key) {
        filtered.sort((a, b) => {
          let aValue, bValue;
          if (sortConfig.key === "role") {
            aValue = a.roles?.[0]?.title?.toLowerCase() || "";
            bValue = b.roles?.[0]?.title?.toLowerCase() || "";
          } else {
            aValue = a[sortConfig.key]?.toLowerCase() || "";
            bValue = b[sortConfig.key]?.toLowerCase() || "";
          }

          if (aValue < bValue)
            return sortConfig.direction === "ascending" ? -1 : 1;
          if (aValue > bValue)
            return sortConfig.direction === "ascending" ? 1 : -1;
          return 0;
        });
      }

      setFilteredUsers(filtered);
    }
  }, [filters, activeFilters, users, searchQuery, sortConfig]);

  // Calculate pagination indices
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const handleBackdropClick = (e) => {
    // Only close if the click started and ended on the backdrop
    if (e.target === e.currentTarget && e.type === "mousedown") {
      setIsModalOpen(false);
      setThisUser({ ...defaultUser });
    }
  };

  // Sort function
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await roleService.getRoles();
        setRoles(rolesData.data);
      } catch (error) {
        showToast({
          message: "Failed to load roles",
          type: "error",
        });
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);

  // Handle user edit
  const handleEditUser = (user) => {
    setThisUser({
      ...user,
      color: user.color || "#ffffff",
      password: "",
      roles: user.roles?.[0]?.id || "",
    });
    setIsModalOpen(true);
  };

  // Handle user update
  const handleUpdateUser = () => {
    if (!thisUser.roles) {
      showToast({
        message: "Please select a role.",
        type: "error",
      });
      return;
    }
    updateUser(
      {
        id: thisUser.id,
        userData: {
          name: thisUser.name,
          password: thisUser.password || undefined,
          color: thisUser.color,
          roles: thisUser.roles ? thisUser.roles : 0,
          company_id: thisUser.company_id || undefined,
          company_logins: thisUser.company_logins || undefined,
          city: thisUser.city || undefined,
          country: thisUser.country || undefined,
        },
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          setThisUser({ ...defaultUser });
          refetch();
          showToast({
            message: "User updated successfully!",
            type: "success",
          });
        },
        onError: (error) => {
          showToast({
            message: error.response?.data?.message || "Failed to update user.",
            type: "error",
          });
        },
      },
    );
  };

  // Extract pagination data from API response
  useEffect(() => {
    if (usersData) {
      // Set filtered users from API data
      setFilteredUsers(Array.isArray(usersData?.data) ? usersData.data : []);

      // Set pagination data from API response
      if (usersData.paginations) {
        setServerPagination(usersData.paginations);
      }
    }
  }, [usersData]);

  // Update handlePageChange to properly trigger a new API call
  const handlePageChange = (page) => {
    // Just update the pagination state - the query will automatically refetch
    setServerPagination((prev) => ({
      ...prev,
      current_page: page,
    }));
    // No need to manually call refetch() here
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading users..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Users
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage users, view account information, and track system access
                and roles.
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setIsModalOpen(true);
              }}
              className="bg-primary-dark text-white px-4 h-[36px] rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark"
              id="add-user-btn"
            >
              Add User
            </button>
          </div>
          <div className="w-full md:w-[384px]">
            <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                id="user-search-input"
                type="text"
                placeholder="Search"
                value={tempSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
                autoComplete="off"
              />
            </div>
          </div>
        </div>

        {isModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
            onMouseDown={handleBackdropClick}
          >
            <UserForm
              user={thisUser}
              updateUser={setThisUser}
              roles={roles}
              submitUser={handleSubmitUser}
              closeModal={() => setIsModalOpen(false)}
              isEditMode={!!thisUser.id}
            />
          </div>
        )}

        <DialogBox
          isOpen={userToDelete !== null}
          onClose={() => setUserToDelete(null)}
          title="Delete User"
          className="w-[40%] max-md:w-[60%]"
          actionButton={
            <button
              onClick={() => handleDeleteUser(userToDelete)}
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
              disabled={isDeleteButtonDisabled}
            >
              Delete
            </button>
          }
        >
          <p className="text-muted-foreground text-sm mt-1 max-w-[350px]">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
        </DialogBox>

        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full divide-y divide-border bg-card">
            <thead className="bg-muted border-b border-border">
              <tr className="text-left">
                <th
                  className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <div className="inline-flex flex-col space-y-[2px] text-muted-foreground">
                      {sortConfig.key === "name" ? (
                        sortConfig.direction === "ascending" ? (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        )
                      ) : (
                        <>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        </>
                      )}
                    </div>
                  </div>
                </th>
                <th
                  className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("email")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Email</span>
                    <div className="inline-flex flex-col space-y-[2px] text-muted-foreground">
                      {sortConfig.key === "email" ? (
                        sortConfig.direction === "ascending" ? (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        )
                      ) : (
                        <>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        </>
                      )}
                    </div>
                  </div>
                </th>
                <th
                  className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("role")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Role</span>
                    <div className="inline-flex flex-col space-y-[2px] text-muted-foreground">
                      {sortConfig.key === "role" ? (
                        sortConfig.direction === "ascending" ? (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        )
                      ) : (
                        <>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        </>
                      )}
                    </div>
                  </div>
                </th>
                <th
                  className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("city")}
                >
                  <div className="flex items-center space-x-1">
                    <span>City</span>
                    <div className="inline-flex flex-col space-y-[2px] text-muted-foreground">
                      {sortConfig.key === "city" ? (
                        sortConfig.direction === "ascending" ? (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        )
                      ) : (
                        <>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        </>
                      )}
                    </div>
                  </div>
                </th>
                <th
                  className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("country")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Country</span>
                    <div className="inline-flex flex-col space-y-[2px] text-muted-foreground">
                      {sortConfig.key === "country" ? (
                        sortConfig.direction === "ascending" ? (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                        ) : (
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        )
                      ) : (
                        <>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 0L0 5H10L5 0Z" />
                          </svg>
                          <svg
                            width="10"
                            height="5"
                            viewBox="0 0 10 5"
                            fill="currentColor"
                          >
                            <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" />
                          </svg>
                        </>
                      )}
                    </div>
                  </div>
                </th>
                <th className="px-6 py-5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filteredUsers
                .slice(indexOfFirstItem, indexOfLastItem)
                .map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-muted transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-md font-medium text-foreground">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground capitalize">
                      {user.roles?.title === "Company_admin" ? (
                        <span className="text-red-500">Company Admin</span>
                      ) : user.roles?.[0]?.title === "Company" ? (
                        <span>Client Dashboard Only</span>
                      ) : (
                        user.roles?.[0]?.title?.replace("_", " ") || "No Role"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {user.city || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {user.country || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap flex items-center space-x-2">
                      <Edit
                        className="h-5 w-5 cursor-pointer text-muted-foreground hover:text-muted-foreground"
                        onClick={() => handleEditUser(user)}
                      />
                      <Trash2
                        className="h-5 w-5 cursor-pointer text-red-500 hover:text-red-700"
                        onClick={() => setUserToDelete(user.id)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="w-[90%] mx-auto p-6">
        <Pagination
          currentPage={serverPagination.current_page || 1}
          totalPages={serverPagination.last_page || 1}
          per_page={serverPagination.per_page || itemsPerPage}
          total={serverPagination.total || 0}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default Users;
